/* La pagina "Fai la fattura": quattro passi, sempre gli stessi, per una fattura
 * normale come per l'acconto o il saldo.
 *
 * Dal 15 ottobre 2026 sera. La prima versione (una casella di testo, una colonna
 * "Adesso" con quattro frasi, due avvisi, una tabella) era un muro: chi fattura
 * doveva leggere tutto per capire cosa fare, e il campo del numero finiva sotto
 * la piega. Qui si ragiona da chi ci lavora ogni giorno:
 *
 * 1. **Un solo gesto per passo, in ordine.** Copia il testo, copia l'importo,
 *    controlla il totale, scrivi il numero. Un passo copiato si segna fatto, cosi'
 *    si vede cosa manca senza ricordarselo.
 * 2. **Il numero e' il passo che chiude, e lo protegge il programma.** Mostra
 *    l'ultimo numero registrato, rifiuta un numero gia' usato (una fattura non
 *    si registra due volte) e avvisa se il numero non e' quello che di solito
 *    segue. Un numero sbagliato non lo rilegge nessuno.
 * 3. **La scadenza dei 12 giorni sta nel titolo**, dove la si vede subito, e la
 *    posizione in coda ("2 di 5") dice quanto resta. Dopo il numero si passa alla
 *    fattura successiva, la piu' urgente.
 * 4. **Le spiegazioni non ci sono, o stanno chiuse.** I conti (cassa, IVA) sono
 *    dietro "Vedi i conti": servono a chi ha un dubbio, non a chi segue i passi.
 *
 * Il componente non sa da dove vengono i dati: due adattatori (`FatturaAcconto`,
 * `FatturaNormale`) li caricano e gli passano come `DatiPassi`.
 */

import { useEffect, useState, type FormEvent } from "react";
import { ErroreApi } from "../api";
import { dataBreve, type UltimaVolta } from "../dominio";
import { TastoCopia, copiaNegliAppunti } from "./SchedaCopia";

export interface DatiPassi {
  /** Il cliente, come titolo. */
  chi: string;
  /** "Fattura", "Fattura sull'acconto", "Fattura sul saldo". */
  cosa: string;
  /** Una riga che dice da dove nasce: "Incassati 200,00 il 15 ottobre, bonifico". */
  contesto: string;
  scadenza: { testo: string; entro: string; urgenza: "in_tempo" | "avviso" | "scaduto" };
  testo: string;
  /** Se il testo ritoccato si puo' salvare (solo prima che la fattura parta). */
  modificabile: boolean;
  importoRiga: string;
  /** Il totale che Fatture Smart deve dare. */
  totaleAtteso: string;
  /** Cassa, imponibile, IVA e totale, per chi vuole riguardarli. */
  controllo: { etichetta: string; valore: string }[];
  iva: string;
  /** Una frase sui conti, per chi apre "Vedi i conti". */
  notaConti?: string;
  /** Cose da guardare sul testo: poche, e solo se servono davvero. */
  avvertenze: string[];
  ultimaVolta?: UltimaVolta | null;
  /** Il numero, se la fattura e' gia' stata registrata. */
  numeroRegistrato?: string;
}

function messaggio(e: unknown, ripiego: string): string {
  return e instanceof ErroreApi ? e.message : ripiego;
}

function righeDi(testo: string): number {
  return Math.min(7, Math.max(4, testo.split("\n").length + 1));
}

/** L'ultimo numero registrato, fra quelli fatti solo di cifre ("93", non "1/PA"). */
export function ultimoNumero(usati: string[]): number | null {
  const cifre = usati.filter((n) => /^\d+$/.test(n.trim())).map((n) => Number(n.trim()));
  return cifre.length > 0 ? Math.max(...cifre) : null;
}

export function PassiFattura({
  dati,
  posizione,
  numeriUsati,
  chiudi,
  salvaTesto,
  registra,
  vediAvviso,
}: {
  dati: DatiPassi;
  posizione: { i: number; n: number } | null;
  /** I numeri gia' registrati, per rifiutare un doppione e suggerire quello dopo. */
  numeriUsati: string[];
  chiudi: () => void;
  /** Presente solo dove il testo ritoccato si salva (la fattura normale, non l'acconto). */
  salvaTesto?: (testo: string) => Promise<void>;
  /** Registra il numero e porta alla fattura successiva: lo decide chi chiama. */
  registra: (numero: string) => Promise<void>;
  vediAvviso?: () => void;
}) {
  const [bozza, setBozza] = useState<string | null>(null);
  const [daUltimaVolta, setDaUltimaVolta] = useState(false);
  const [testoCopiato, setTestoCopiato] = useState(false);
  const [importoCopiato, setImportoCopiato] = useState(false);
  const [numero, setNumero] = useState("");
  const [conferma, setConferma] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [nonRiuscito, setNonRiuscito] = useState(false);

  // Se arriva un testo diverso (salvato, o un'altra fattura) si riparte da quello.
  useEffect(() => {
    setBozza(null);
    setDaUltimaVolta(false);
  }, [dati.testo]);

  const testo = bozza ?? dati.testo;
  const ritoccato = bozza !== null && bozza !== dati.testo;
  const scritto = numero.trim();
  const doppione = scritto !== "" && numeriUsati.some((n) => n.trim() === scritto);
  const ultimo = ultimoNumero(numeriUsati);
  const dopoLUltimo = /^\d+$/.test(scritto) && ultimo !== null && Number(scritto) !== ultimo + 1;

  async function copiaIlTesto() {
    setErrore(null);
    setNonRiuscito(false);
    if (!(await copiaNegliAppunti(testo))) {
      setNonRiuscito(true);
      return;
    }
    setTestoCopiato(true);
    // Chi ritocca e copia fa un gesto solo: quello che e' stato incollato e' quello che si ricorda.
    if (ritoccato && dati.modificabile && salvaTesto) {
      try {
        await salvaTesto(testo);
      } catch (e) {
        setErrore(messaggio(e, "Copiato, ma non sono riuscito a salvare i ritocchi."));
      }
    }
  }

  function chiedi(evento: FormEvent) {
    evento.preventDefault();
    setErrore(null);
    if (!scritto) return setErrore("Scrivi il numero che Fatture Smart ha dato alla fattura.");
    if (doppione) return;
    setConferma(true);
  }

  async function conferma_() {
    setErrore(null);
    setInCorso(true);
    try {
      await registra(scritto);
    } catch (e) {
      setErrore(messaggio(e, "Non sono riuscito a registrare il numero."));
      setConferma(false);
      setInCorso(false);
    }
  }

  const ultima = dati.ultimaVolta;
  // I conti partono dal compenso (la riga) cosi' si risale a imponibile e totale; il totale non si
  // ripete: e' gia' nel titolo del passo.
  const soloCifre = [
    { etichetta: "Compenso (importo della riga)", valore: dati.importoRiga },
    ...dati.controllo.filter((v) => v.etichetta !== "Importo della riga" && v.etichetta !== "Totale documento"),
  ];

  return (
    <article className="documento passi-fattura">
      <div className="passi-testa">
        <button type="button" className="tasto tasto-nudo torna" onClick={chiudi}>
          ‹ Torna all'elenco
        </button>
        {posizione && (
          <span className="narrativa">
            Fattura {posizione.i} di {posizione.n}
          </span>
        )}
      </div>

      <header className="foglio passi-titolo">
        <h2>{dati.chi || "Nessun intestatario registrato"}</h2>
        <p className="passi-cosa">
          {dati.cosa}, totale <strong>{dati.totaleAtteso}</strong> euro
        </p>
        <p className={`passi-scadenza urgenza-${dati.scadenza.urgenza}`}>
          Da fare entro il {dati.scadenza.entro}: {dati.scadenza.testo}
        </p>
        {dati.contesto && <p className="narrativa">{dati.contesto}</p>}
      </header>

      {dati.numeroRegistrato ? (
        <p className="riga-fatto">Già registrata: fattura n. {dati.numeroRegistrato}.</p>
      ) : (
        <ol className="fasi">
          <li className={`fase${testoCopiato ? " fatto" : ""}`}>
            <div className="fase-testa">
              <span className="fase-numero" aria-hidden="true">
                {testoCopiato ? "✓" : "1"}
              </span>
              <h3>
                <label htmlFor="passi-testo">Copia il testo</label>
              </h3>
              <button type="button" className="tasto tasto-principale" onClick={() => void copiaIlTesto()}>
                {nonRiuscito ? "Copia non riuscita" : testoCopiato ? "Copiato" : "Copia il testo"}
              </button>
            </div>
            {dati.avvertenze.map((riga) => (
              <p key={riga} className="riga-avviso">
                {riga.charAt(0).toUpperCase() + riga.slice(1)}
              </p>
            ))}
            <textarea
              id="passi-testo"
              className="testo-fattura-area"
              value={testo}
              readOnly={!dati.modificabile}
              rows={righeDi(testo)}
              spellCheck={false}
              onChange={(e) => {
                setBozza(e.target.value);
                setDaUltimaVolta(false);
                setTestoCopiato(false);
              }}
            />
            {dati.modificabile && ultima && !daUltimaVolta && (
              <button
                type="button"
                className="tasto tasto-nudo"
                onClick={() => {
                  setBozza(ultima.testo);
                  setDaUltimaVolta(true);
                  setTestoCopiato(false);
                }}
              >
                Usa il testo dell'ultima volta (
                {ultima.tipo === "FATTURA" ? `fattura${ultima.numero ? ` n. ${ultima.numero}` : ""}` : "avviso"}{" "}
                del {dataBreve(ultima.data)})
              </button>
            )}
            {daUltimaVolta && ultima && (
              <>
                {ultima.periodo_avanzato && (
                  <p className="riga-avviso">Il periodo nella prima riga è stato portato avanti: controllalo.</p>
                )}
                {ultima.anni.length > 0 && (
                  <p className="riga-avviso">
                    Nelle voci compaiono gli anni {ultima.anni.join(", ")}: sono quelli dell'ultima volta.
                  </p>
                )}
              </>
            )}
            {ritoccato && dati.modificabile && salvaTesto && (
              <p className="narrativa">I ritocchi si salvano quando copi.</p>
            )}
            {ritoccato && !salvaTesto && <p className="narrativa">I ritocchi valgono solo per questa copia.</p>}
          </li>

          <li className={`fase${importoCopiato ? " fatto" : ""}`}>
            <div className="fase-testa">
              <span className="fase-numero" aria-hidden="true">
                {importoCopiato ? "✓" : "2"}
              </span>
              <h3>
                Importo della riga <span className="fase-valore">{dati.importoRiga}</span>
              </h3>
              <span onClickCapture={() => setImportoCopiato(true)}>
                <TastoCopia testo={dati.importoRiga} />
              </span>
            </div>
          </li>

          <li className="fase">
            <div className="fase-testa">
              <span className="fase-numero" aria-hidden="true">
                3
              </span>
              <h3>
                Il totale in Fatture Smart deve venire <span className="fase-valore">{dati.totaleAtteso}</span>
              </h3>
            </div>
            <details className="fase-dettaglio">
              <summary>Vedi i conti</summary>
              <table className="tabella copia-tabella">
                <tbody>
                  {soloCifre.map((v) => (
                    <tr key={v.etichetta} className={v.etichetta === "Totale documento" ? "totale" : undefined}>
                      <td className="copia-etichetta">
                        {v.etichetta}
                        {v.etichetta === "IVA" && <span className="narrativa">{dati.iva}</span>}
                      </td>
                      <td className="cifra">{v.valore}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {dati.notaConti && <p className="narrativa">{dati.notaConti}</p>}
            </details>
          </li>

          <li className="fase fase-chiude">
            <form onSubmit={chiedi} noValidate>
              <div className="fase-testa">
                <span className="fase-numero" aria-hidden="true">
                  4
                </span>
                <h3>
                  <label htmlFor="passi-numero">Numero che ti ha dato Fatture Smart</label>
                </h3>
              </div>
              <div className="fase-numero-riga">
                <input
                  id="passi-numero"
                  value={numero}
                  placeholder={ultimo !== null ? `dopo la ${ultimo}` : "per esempio 93"}
                  disabled={conferma || inCorso}
                  autoComplete="off"
                  onChange={(e) => setNumero(e.target.value)}
                />
                {!conferma && (
                  <button className="tasto tasto-principale" type="submit" disabled={doppione}>
                    Registra il numero
                  </button>
                )}
              </div>
              {doppione && (
                <p className="errore" role="alert">
                  Il numero {scritto} è già registrato su un'altra fattura.
                </p>
              )}
              {!doppione && dopoLUltimo && !conferma && (
                <p className="riga-avviso">
                  L'ultima registrata è la {ultimo}: di solito segue la {Number(ultimo) + 1}. Controlla il numero.
                </p>
              )}
              {conferma && (
                <div className="fase-conferma">
                  <p>
                    Registro la <strong>fattura n. {scritto}</strong> per {dati.chi || "questo cliente"}, totale{" "}
                    {dati.totaleAtteso} euro?
                  </p>
                  <div className="tasti">
                    <button
                      type="button"
                      className="tasto tasto-principale"
                      disabled={inCorso}
                      onClick={() => void conferma_()}
                    >
                      {inCorso ? "Sto registrando" : "Sì, è fatta"}
                    </button>
                    <button
                      type="button"
                      className="tasto tasto-nudo"
                      disabled={inCorso}
                      onClick={() => setConferma(false)}
                    >
                      No, torna indietro
                    </button>
                  </div>
                </div>
              )}
            </form>
          </li>
        </ol>
      )}

      {errore && (
        <p className="errore" role="alert">
          {errore}
        </p>
      )}
      {vediAvviso && (
        <p className="passi-piede">
          <button type="button" className="tasto tasto-nudo" onClick={vediAvviso}>
            Vedi l'avviso
          </button>
        </p>
      )}
    </article>
  );
}
